import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'teacher' | 'student' | 'parent';
  phone?: string;
  profileImage?: string;
  roleData?: any;
  schoolName?: string;
  schoolCode?: string;
}

interface School {
  id: string;
  name: string;
  code: string;
  subdomain: string;
  country?: string;
  currency?: string;
  currencySymbol?: string;
  contactEmail?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  maxStudents?: number;
  maxTeachers?: number;
  settings?: any;
}

interface AuthContextType {
  user: User | null;
  school: School | null;
  token: string | null;
  login: (email: string, password: string, schoolCode: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    const loadUser = async () => {
      const savedSchoolCode = localStorage.getItem('schoolCode');
      
      if (token && savedSchoolCode) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          axios.defaults.headers.common['X-School-Code'] = savedSchoolCode;
          const response = await axios.get(`${API_URL}/auth/me`);
          
          // Add school info to user object
          const userData = response.data.user;
          const schoolData = response.data.school;
          if (schoolData) {
            userData.schoolName = schoolData.name;
            userData.schoolCode = schoolData.code;
          }
          
          setUser(userData);
          setSchool(schoolData);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('schoolCode');
          setToken(null);
          setSchool(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token, API_URL]);

  const login = async (email: string, password: string, schoolCode: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { 
        email, 
        password,
        schoolCode 
      });
      const { token: newToken, user: newUser, school: newSchool } = response.data;
      
      // Add school info to user object for easier access
      newUser.schoolName = newSchool.name;
      newUser.schoolCode = newSchool.code;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('schoolCode', newSchool.code);
      localStorage.setItem('schoolName', newSchool.name);
      
      setToken(newToken);
      setUser(newUser);
      setSchool(newSchool);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      axios.defaults.headers.common['X-School-Code'] = newSchool.code;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('schoolCode');
    localStorage.removeItem('schoolName');
    setToken(null);
    setUser(null);
    setSchool(null);
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-School-Code'];
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  return (
    <AuthContext.Provider value={{ user, school, token, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
